import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Search } from 'lucide-react';
import Card from '../../components/Card';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/apiService';
import EquipmentDetailSummary from '../../components/EquipmentDetailSummary';
import { buildClientVehicleTableColumns } from '../../utils/clientEquipmentTableColumns';

const CLIENT_TABLE_EXCLUDE = ['clientName', 'clientCompany'];

const VehicleEquipmentView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewItem, setViewItem] = useState(null);
  const { error: showError } = useToast();

  const columns = useMemo(
    () => buildClientVehicleTableColumns((row) => setViewItem(row)),
    []
  );

  useEffect(() => {
    if (!user?.id) return;
    loadEquipment();
  }, [user?.id]);

  useEffect(() => {
    applyFilters();
  }, [equipment, searchQuery]);

  const loadEquipment = async () => {
    try {
      setLoading(true);
      const data = await apiService.getVehicleEquipment(user?.id, null);
      setEquipment(data);
    } catch (error) {
      showError('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = equipment;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.vehicleRegistration?.toLowerCase().includes(query) ||
          item.serialNumber?.toLowerCase().includes(query) ||
          item.affectation?.toLowerCase().includes(query) ||
          item.structure?.toLowerCase().includes(query)
      );
    }

    setFilteredEquipment(filtered);
  };

  const kpiStats = useMemo(() => {
    let controlled = 0;
    let toControl = 0;
    filteredEquipment.forEach((e) => {
      if (e.lastMaintenanceDate) controlled += 1;
      else toControl += 1;
    });
    return { total: filteredEquipment.length, controlled, toControl };
  }, [filteredEquipment]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Truck className="w-8 h-8 text-red-600" />
          Équipements Véhicules
        </h1>
        <p className="text-gray-600 mt-1">
          Vue synthétique — détail complet via « Voir » ou la fiche équipement
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        <Card>
          <div className="text-center py-1">
            <p className="text-2xl sm:text-3xl font-bold text-red-600">{kpiStats.total}</p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Total véhicules</p>
          </div>
        </Card>
        <Card>
          <div className="text-center py-1">
            <p className="text-2xl sm:text-3xl font-bold text-green-600">{kpiStats.controlled}</p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Contrôlés</p>
          </div>
        </Card>
        <Card>
          <div className="text-center py-1">
            <p className="text-2xl sm:text-3xl font-bold text-amber-600">{kpiStats.toControl}</p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">À contrôler</p>
          </div>
        </Card>
      </div>

      <Card>
        <Input
          placeholder="Rechercher par matricule, N° série, affectation…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={Search}
        />
      </Card>

      <Card className="p-0 sm:p-0 overflow-hidden w-full min-w-0">
        <Table
          columns={columns}
          data={filteredEquipment}
          emptyMessage="Aucun équipement véhicule enregistré"
        />
      </Card>

      <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title="Détails équipement véhicule">
        {viewItem && (
          <div className="space-y-4">
            <div className="max-h-[60vh] overflow-y-auto">
              <EquipmentDetailSummary equipment={viewItem} columns={2} dense excludeKeys={CLIENT_TABLE_EXCLUDE} />
            </div>
            <Button
              variant="primary"
              className="w-full"
              onClick={() => {
                setViewItem(null);
                navigate(`/my-equipment/${viewItem.id}`);
              }}
            >
              Ouvrir la fiche complète
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VehicleEquipmentView;
